/**
 * Shared UI primitives.
 *
 * Deliberately small and hand-written — see the note at the top of styles.css
 * for why no component library is used here.
 */

import {
  createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState,
  type ButtonHTMLAttributes, type CSSProperties, type InputHTMLAttributes, type ReactNode,
  type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { money as fmtMoney } from '../lib/format';
import { fetchBlob } from '../lib/api';
import { useT } from '../lib/i18n';

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'ghost' | 'quiet' | 'danger' | 'ok' | 'warn';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  /** Shows a spinner and disables the button. */
  busy?: boolean;
  icon?: ReactNode;
}

export function Button({
  variant = 'primary', size = 'md', block, busy, icon, children, className = '', disabled, ...rest
}: ButtonProps) {
  const variantClass = variant === 'primary' ? '' : `btn--${variant}`;
  const sizeClass = size === 'md' ? '' : `btn--${size}`;

  return (
    <button
      type={rest.type ?? 'button'}
      className={['btn', variantClass, sizeClass, block ? 'btn--block' : '', className]
        .filter(Boolean).join(' ')}
      disabled={disabled || busy}
      // Announced to screen readers, and prevents a double-tap submitting twice.
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <span className="spinner" aria-hidden /> : icon}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Form fields
// ---------------------------------------------------------------------------

interface FieldWrapProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: (id: string) => ReactNode;
}

function FieldWrap({ label, hint, error, required, children }: FieldWrapProps) {
  const id = useId();
  const t = useT();
  return (
    <div className="field">
      {label && (
        <label htmlFor={id}>
          {label}
          {!required && <span className="t-faint t-xs"> · {t('app.optional')}</span>}
        </label>
      )}
      {children(id)}
      {/* Errors replace the hint rather than stacking, so the field does not
          jump when validation fires. */}
      {error ? <span className="field__error">{error}</span>
        : hint ? <span className="field__hint">{hint}</span> : null}
    </div>
  );
}

interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label?: string;
  hint?: string;
  error?: string;
  numeric?: boolean;
}

export function TextInput({ label, hint, error, numeric, required, ...rest }: TextInputProps) {
  return (
    <FieldWrap label={label} hint={hint} error={error} required={required}>
      {(id) => (
        <input
          id={id}
          className={numeric ? 'input input--num' : 'input'}
          aria-invalid={error ? true : undefined}
          // Numeric keypad on Android without the type=number spinner quirks.
          inputMode={numeric ? 'decimal' : rest.inputMode}
          {...rest}
        />
      )}
    </FieldWrap>
  );
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  label?: string;
  hint?: string;
  error?: string;
  options: {
    value: string;
    label: string;
    /**
     * Shown but unselectable.
     *
     * Used where an option exists and is unavailable for a reason the person
     * needs to know — a van another driver already has open today. Hiding it
     * instead makes the list look wrong: "where is JH01AB1234?" has an answer,
     * and an absent row does not give it.
     */
    disabled?: boolean;
  }[];
  placeholder?: string;
}

export function Select({ label, hint, error, options, placeholder, required, ...rest }: SelectProps) {
  return (
    <FieldWrap label={label} hint={hint} error={error} required={required}>
      {(id) => (
        <select id={id} className="select" aria-invalid={error ? true : undefined} {...rest}>
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>
          ))}
        </select>
      )}
    </FieldWrap>
  );
}

interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  label?: string;
  hint?: string;
  error?: string;
}

export function TextArea({ label, hint, error, required, ...rest }: TextAreaProps) {
  return (
    <FieldWrap label={label} hint={hint} error={error} required={required}>
      {(id) => (
        <textarea id={id} className="textarea" aria-invalid={error ? true : undefined} {...rest} />
      )}
    </FieldWrap>
  );
}

/**
 * Rupee amount input that stores paise.
 *
 * CALCULATOR-STYLE, unified with the payment section (PaymentCard's MoneyEntry)
 * on request so every amount field in the app behaves identically to the
 * collection screen: a ₹ sits in the field, the digits fill in FROM THE RIGHT,
 * and the last two are always paise — so ₹1,50,000.00 is typed "15000000" and a
 * stray decimal point can never drop a figure into the wrong column. The display
 * is Indian-grouped via money() (symbol drawn separately so it does not scroll
 * with the text). Empty → null, preserving every caller's existing semantics.
 *
 * Was a plain "type the rupees.paise yourself" field. Two different money-entry
 * styles side by side (this vs the pay screen) confused people entering amounts,
 * so they are now one. Fully controlled on valuePaise — no local string state,
 * exactly like MoneyEntry.
 */
export function MoneyInput({
  valuePaise, onChangePaise, label, hint, error, required, placeholder,
}: {
  valuePaise: number | null;
  onChangePaise: (paise: number | null) => void;
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
}) {
  const display = valuePaise && valuePaise > 0 ? fmtMoney(valuePaise, { symbol: false }) : '';
  return (
    <FieldWrap label={label} hint={hint} error={error} required={required}>
      {(id) => (
        <div className={`moneyin${error ? ' moneyin--err' : ''}`}>
          <span className="moneyin__cur" aria-hidden="true">₹</span>
          <input
            id={id}
            className="moneyin__in"
            type="text"
            inputMode="numeric"
            placeholder={placeholder ?? '0.00'}
            aria-invalid={error ? true : undefined}
            value={display}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
              onChangePaise(digits ? parseInt(digits, 10) : null);
            }}
          />
        </div>
      )}
    </FieldWrap>
  );
}

/** Plus/minus stepper. Avoids the keyboard entirely for cart quantities. */
export function Stepper({
  value, onChange, min = 0, max = 99999, step = 1, disabled,
}: {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
}) {
  const clamp = (n: number) => Math.max(min, Math.min(max, n));
  return (
    <div className="stepper">
      <button
        type="button" aria-label="Decrease" disabled={disabled || value <= min}
        onClick={() => onChange(clamp(value - step))}
      >−</button>
      <input
        type="text" inputMode="numeric" value={value} disabled={disabled}
        aria-label="Quantity"
        onChange={(e) => {
          const n = parseInt(e.target.value.replace(/\D/g, ''), 10);
          onChange(Number.isFinite(n) ? clamp(n) : min);
        }}
      />
      <button
        type="button" aria-label="Increase" disabled={disabled || value >= max}
        onClick={() => onChange(clamp(value + step))}
      >+</button>
    </div>
  );
}

/** Segmented control for two or three mutually exclusive options. */
export function Segment<T extends string>({
  value, onChange, options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="segment" role="tablist">
      {options.map((o) => (
        <button
          key={o.value} type="button" role="tab"
          aria-pressed={value === o.value}
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
        >{o.label}</button>
      ))}
    </div>
  );
}

/** Horizontally scrolling single-select chip row, used for filters. */
export function ChipRow<T extends string>({
  value, onChange, options, wrap,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string; count?: number }[];
  /**
   * Wrap onto multiple lines instead of scrolling sideways. For a short, fixed
   * set — the handful of product categories — wrapping shows every option at
   * once on a phone, where a sideways scroll hides the last ones off the edge.
   */
  wrap?: boolean;
}) {
  return (
    <div className={['chiprow', wrap ? 'chiprow--wrap' : ''].filter(Boolean).join(' ')}>
      {options.map((o) => (
        <button
          key={o.value} type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
          {o.count !== undefined && o.count > 0 && <span className="t-xs"> · {o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Switch({
  checked, onChange, label, hint,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
}) {
  const id = useId();
  return (
    <div className="switch">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <label htmlFor={id} className="grow">
        <span className="t-semi">{label}</span>
        {hint && <div className="t-xs t-muted">{hint}</div>}
      </label>
    </div>
  );
}

/**
 * A titled group of fields on a form.
 *
 * Used to break a long single-column form — the customer card — into the
 * sections a person reads it in (who they are, where they are, how they are
 * classified) rather than one undifferentiated scroll.
 */
export function FormSection({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="form-section">
      {title && <div className="form-section__title">{title}</div>}
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Layout & display
// ---------------------------------------------------------------------------

export function Card({
  children, className = '', flush, onClick, as,
}: {
  children: ReactNode;
  className?: string;
  flush?: boolean;
  onClick?: () => void;
  as?: 'div' | 'section';
}) {
  const Tag = as ?? 'div';
  return (
    <Tag
      className={['card', flush ? 'card--flush' : '', onClick ? 'card--tap' : '', className]
        .filter(Boolean).join(' ')}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      {children}
    </Tag>
  );
}

export function Chip({
  children, tone = 'neutral', outline,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'ok' | 'warn' | 'danger' | 'info';
  outline?: boolean;
}) {
  const toneClass = tone === 'neutral' ? '' : `chip--${tone}`;
  return (
    <span className={['chip', toneClass, outline ? 'chip--outline' : ''].filter(Boolean).join(' ')}>
      {children}
    </span>
  );
}

export function Banner({
  tone = 'info', title, children, action,
}: {
  tone?: 'info' | 'ok' | 'warn' | 'danger';
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={`banner banner--${tone}`} role={tone === 'danger' ? 'alert' : undefined}>
      <div className="grow">
        {title && <div className="banner__title">{title}</div>}
        {children}
      </div>
      {action}
    </div>
  );
}

/** KPI tile for the admin dashboard. */
/**
 * A KPI tile, optionally the control that takes you to the thing it counts.
 *
 * `onClick` rather than a route, deliberately: this module is shared with
 * screens that render outside a router, and importing react-router here to
 * save a caller three characters would make Tile impossible to use in a
 * PDF preview or a test harness. The caller already has `useNavigate`.
 *
 * When it IS clickable it renders a real <button>, so it is reachable by
 * keyboard and announced as a control. A div with an onClick looks identical
 * and cannot be tabbed to — and the whole point of this change was that a
 * number showing "3 approvals" should be the way to reach those approvals.
 */
export function Tile({
  label, value, meta, tone, onClick, hint,
}: {
  label: string;
  value: ReactNode;
  meta?: ReactNode;
  /**
   * Two kinds of tone. The first three are STATUS — they mean something is wrong
   * ('critical'/'warn') or fine ('ok'). The palette hues are CATEGORICAL — they
   * group tiles by subject (today's run = blue, money = green, …) to match the
   * admin dashboard's colour system, on the field dashboards. A categorical hue
   * carries no judgement; never use it where a status tone belongs.
   */
  tone?: 'ok' | 'warn' | 'critical' | 'blue' | 'green' | 'amber' | 'teal' | 'purple' | 'pink';
  onClick?: () => void;
  /** Accessible name for the action, e.g. "Open approvals". */
  hint?: string;
}) {
  const toneClass =
    tone === 'critical' ? 'tile--critical'
      : tone === 'warn' ? 'tile--warn'
      : tone === 'ok' || !tone ? ''
      : `tile--${tone}`;
  const inner = (
    <>
      <span className="tile__label">{label}</span>
      <span className="tile__value">{value}</span>
      {meta && <span className="tile__meta">{meta}</span>}
    </>
  );

  if (!onClick) {
    return <div className={['tile', toneClass].filter(Boolean).join(' ')}>{inner}</div>;
  }

  return (
    <button
      type="button"
      className={['tile', 'tile--link', toneClass].filter(Boolean).join(' ')}
      onClick={onClick}
      aria-label={hint ? `${label}: ${hint}` : undefined}
    >
      {inner}
    </button>
  );
}

/** Money display. Takes paise, never rupees. */
export function Money({
  paise, compact, className = '', tone,
}: {
  paise: number | null | undefined;
  compact?: boolean;
  className?: string;
  tone?: 'ok' | 'warn' | 'danger' | 'muted';
}) {
  const toneClass = tone === 'ok' ? 't-ok'
    : tone === 'warn' ? 't-warn'
    : tone === 'danger' ? 't-danger'
    : tone === 'muted' ? 't-muted' : '';
  return (
    <span className={['money', toneClass, className].filter(Boolean).join(' ')}>
      {fmtMoney(paise, { compact })}
    </span>
  );
}

export function Empty({ title, hint, action }: { title?: string; hint?: string; action?: ReactNode }) {
  const t = useT();
  return (
    <div className="empty">
      <div className="empty__title">{title ?? t('app.noData')}</div>
      {hint && <div className="t-sm">{hint}</div>}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

/** Skeleton rows. Keeps layout stable so content does not jump on load. */
export function Skeleton({ rows = 3, height = 64 }: { rows?: number; height?: number }) {
  return (
    <div className="stack-2" aria-hidden>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skel" style={{ height }} />
      ))}
    </div>
  );
}

/**
 * The branded "busy" indicator: the GD Strategic logo with a brand-colour ring
 * sweeping around it. Shown whenever the app is loading or working, so a wait
 * feels like the product doing something rather than a blank stall. `small`
 * gives the compact version for inline/card use.
 */
export function Spinner({ label, small }: { label?: string; small?: boolean }) {
  const t = useT();
  return (
    <div className="center" style={{ padding: small ? 12 : 32, gap: 10, flexDirection: 'column', display: 'flex' }}>
      <span className={small ? 'brandspin brandspin--sm' : 'brandspin'} aria-hidden>
        <span className="brandspin__ring" />
        <img className="brandspin__logo" src="/brand-logo.png" alt="" width={small ? 30 : 64} height={small ? 30 : 64} />
      </span>
      {!small && <span className="t-sm t-muted">{label ?? t('app.loading')}</span>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message?: string; onRetry?: () => void }) {
  const t = useT();
  return (
    <Empty
      title={t('app.somethingWrong')}
      hint={message}
      action={onRetry ? <Button variant="ghost" onClick={onRetry}>{t('app.tryAgain')}</Button> : undefined}
    />
  );
}

// ---------------------------------------------------------------------------
// Bottom sheet
//
// Preferred over a centred modal: on a phone it is reachable one-handed, and it
// is where Android users expect confirmations to appear.
// ---------------------------------------------------------------------------

export function Sheet({
  open, onClose, title, children, footer, fullscreen,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Fill the viewport instead of rising from the bottom edge.
   *
   * For a long data-entry form — adding or editing a customer — a bottom
   * sheet is the wrong shape: it is capped at 88vh and reads as a transient
   * confirmation, not the page of fields it actually is. `fullscreen` turns
   * the same panel into a full-height form. Opt-in, so every existing caller
   * keeps the bottom sheet unchanged.
   */
  fullscreen?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Escape closes, and body scroll is locked so the page behind does not move.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="sheet-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={['sheet', fullscreen ? 'sheet--full' : ''].filter(Boolean).join(' ')}
        ref={ref}
      >
        {/* The grip reads as "drag me down to dismiss" — true of a bottom
            sheet, misleading on a full-height form. */}
        {!fullscreen && <div className="sheet__grip" aria-hidden />}
        {title && <h2 style={{ marginBottom: 12 }}>{title}</h2>}
        {children}
        {footer && <div className="row" style={{ marginTop: 20, gap: 8 }}>{footer}</div>}
      </div>
    </div>
  );
}

/** Confirmation sheet. Returns a promise-like via callbacks. */
export function ConfirmSheet({
  open, onClose, onConfirm, title, body, confirmLabel, tone = 'primary', busy,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  tone?: ButtonVariant;
  busy?: boolean;
}) {
  const t = useT();
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" block onClick={onClose} disabled={busy}>{t('app.cancel')}</Button>
          <Button variant={tone} block onClick={onConfirm} busy={busy}>
            {confirmLabel ?? t('app.confirm')}
          </Button>
        </>
      }
    >
      {typeof body === 'string' ? <p className="t-sm t-muted">{body}</p> : body}
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------

type ToastTone = 'neutral' | 'ok' | 'danger';
interface Toast { id: number; message: string; tone: ToastTone }

const ToastContext = createContext<{ show: (message: string, tone?: ToastTone) => void }>({
  show: () => {},
});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const show = useCallback((message: string, tone: ToastTone = 'neutral') => {
    const id = nextId.current++;
    setToasts((prev) => [...prev, { id, message, tone }]);
    // Errors linger a little longer — they usually need reading twice.
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, tone === 'danger' ? 5000 : 3000);
  }, []);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.length > 0 && (
        <div className="toasts" role="status" aria-live="polite">
          {toasts.map((x) => (
            <div key={x.id} className={['toast', x.tone === 'neutral' ? '' : `toast--${x.tone}`].filter(Boolean).join(' ')}>
              {x.message}
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}

// ---------------------------------------------------------------------------
// Authenticated image
// ---------------------------------------------------------------------------

/**
 * Image whose bytes need the bearer token.
 *
 * A POD photo or a UPI screenshot is served by the Worker only to someone
 * entitled to see it, and an `<img src>` cannot carry an Authorization header.
 * So the blob is fetched and handed to the DOM as an object URL — and revoked
 * on unmount, because a long admin session scrolling through screenshots would
 * otherwise hold every one of them in memory.
 */
export function AuthImage({
  path, alt = '', className, style, onClick,
}: {
  path: string | null | undefined;
  alt?: string;
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const t = useT();

  useEffect(() => {
    if (!path) return;
    let objectUrl: string | null = null;
    let cancelled = false;

    void fetchBlob(path)
      .then((next) => {
        if (cancelled) { URL.revokeObjectURL(next); return; }
        objectUrl = next;
        setUrl(next);
      })
      .catch(() => { if (!cancelled) setFailed(true); });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  if (!path || failed) {
    return <div className={['thumb', 'thumb--empty', className].filter(Boolean).join(' ')} style={style} />;
  }
  if (!url) {
    return <div className={['skel', className].filter(Boolean).join(' ')} style={{ width: 84, height: 84, ...style }} aria-label={t('app.loading')} />;
  }
  return (
    <img
      src={url}
      alt={alt}
      className={['thumb', className].filter(Boolean).join(' ')}
      style={onClick ? { cursor: 'zoom-in', ...style } : style}
      onClick={onClick}
    />
  );
}

// ---------------------------------------------------------------------------
// Data table (admin)
// ---------------------------------------------------------------------------

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  numeric?: boolean;
}

/**
 * Table with its own horizontal scroll container.
 *
 * The container is what stops the whole page scrolling sideways on a phone —
 * a report with fourteen columns is unusable otherwise.
 */
export function DataTable<T>({
  columns, rows, keyOf, empty, onRowClick,
}: {
  columns: Column<T>[];
  rows: T[];
  keyOf: (row: T) => string;
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
}) {
  if (rows.length === 0) return <>{empty ?? <Empty />}</>;
  return (
    <div className="tablewrap">
      <table className="data">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.numeric ? 'num' : undefined}>{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={keyOf(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={onRowClick ? { cursor: 'pointer' } : undefined}
            >
              {columns.map((c) => (
                <td key={c.key} className={c.numeric ? 'num' : undefined}>{c.render(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Photo capture tile
// ---------------------------------------------------------------------------

/**
 * Capture-and-preview control.
 *
 * Compression happens in `lib/camera.ts` before the blob ever reaches here, so
 * an 8 MB camera JPEG becomes ~300 KB. That is the difference between a POD
 * that uploads in a shop and one the driver abandons.
 */
export function PhotoTile({
  previewUrl, onCapture, onRemove, required, label, hint, busy,
}: {
  previewUrl: string | null;
  onCapture: () => void;
  onRemove?: () => void;
  required?: boolean;
  label?: string;
  hint?: string;
  busy?: boolean;
}) {
  const t = useT();
  return (
    <div className="field">
      {label && (
        <label>
          {label}
          {required && <span className="t-danger"> *</span>}
        </label>
      )}
      {previewUrl ? (
        <div className="row" style={{ gap: 12 }}>
          <img src={previewUrl} alt="" className="thumb" style={{ width: 84, height: 84, flex: '0 0 84px' }} />
          <div className="stack-2 grow">
            <Button variant="ghost" size="sm" onClick={onCapture} busy={busy}>
              {t('app.retakePhoto')}
            </Button>
            {onRemove && (
              <Button variant="quiet" size="sm" onClick={onRemove}>{t('app.remove')}</Button>
            )}
          </div>
        </div>
      ) : (
        <Button variant="ghost" block onClick={onCapture} busy={busy}>
          {t('app.takePhoto')}
        </Button>
      )}
      {hint && <span className="field__hint">{hint}</span>}
    </div>
  );
}
